import React, { ReactElement, useContext } from 'react'

import Fieldset from '../../Common/Components/Fieldset'
import FormRow from '../../Common/Components/FormRow'
import Heading from '../../Common/Components/Heading'
import { SelectOptions } from '../../Common/Components/Selects'

import { LanguageContext } from '../../Common/Contexts'

import { ScopeFiltering as ScopeFilteringLanguage } from '../../../../i18n'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { ComboBoxAlt } from '../../Common/Components/ComboBox/ComboBoxAlt'
import { HeadingLevel } from '../../Common/Components/Heading/Heading'
import { ANY_VALUE_PARTITION_GUID } from '../../Common/Constants/DefaultValues'
import { RptFilterEntityInfo } from '../../Common/Types/Types'
import { IsVarEmpty } from '../../../NonReact/utils/GlobalFunctions'

/**
 * Component properties type.
 */
type Props = {
    filterEntityInfo: RptFilterEntityInfo | undefined
    telSys: number
    handleTelSysChangeByUser: (newValue: number) => void
    partitions: string[]
    setPartitions: React.Dispatch<React.SetStateAction<string[]>>
}

/**
 * Scope filtering section of Filter tab.
 * @param filterEntityInfo The list of entities according to the filter.
 * @param telSys Report property.
 * @param handleTelSysChangeByUser Enhanced handler for telephone system change.
 * @param partitions Report property.
 * @param setPartitions Setter for corresponding report property.
 */
const ScopeFiltering: React.FC<Props> = ({
    filterEntityInfo,
    telSys,
    handleTelSysChangeByUser,
    partitions,
    setPartitions
}: Props): ReactElement => {
    const language = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle('ScopeFiltering')

    const telSysSelectOptions: SelectOptions<number> = filterEntityInfo
        ? filterEntityInfo.EntityInfo.TelSvrs.Data.map((telephoneSys) => ({
              value: parseInt(telephoneSys.ID, 10),
              label: telephoneSys.Description
          }))
        : []

    const partitionsSelectOptions: SelectOptions<string> = filterEntityInfo
        ? filterEntityInfo.EntityInfo.Partitions.Data.map((partition) => ({
              value: partition.ID,
              label: partition.Description
          }))
        : []

    /**
     * Handles partitions types select box changes. It deselects the '[Any]' item in case one of the normal was selected
     * and automatically selects '[Any]' if all items were unselected
     * @param newValue New selected value.
     */
    const handlePartitionsChange = (newValue: string[]): void => {
        setPartitions((prevValue: string[]) => {
            const prevValueHasAnyOptionSelected = prevValue.indexOf(ANY_VALUE_PARTITION_GUID) !== -1
            const newValueHasAnyOptionSelected = newValue.indexOf(ANY_VALUE_PARTITION_GUID) !== -1
            const anyOptionNewlySelected = !prevValueHasAnyOptionSelected && newValueHasAnyOptionSelected

            if (newValue.length === 0 || anyOptionNewlySelected) {
                return [ANY_VALUE_PARTITION_GUID]
            }
            return newValue.filter((value) => value !== ANY_VALUE_PARTITION_GUID)
        })
    }

    return (
        <Fieldset className='scope-filtering'>
            <Heading level={HeadingLevel.ONE}>
                {language.get(ScopeFilteringLanguage.ScopeFiltering_SectionHeader)}
            </Heading>
            <FormRow>
                <ComboBoxAlt
                    label={language.get(ScopeFilteringLanguage.TelephoneserverLabel)}
                    value={telSys}
                    handleChange={(newValue?: number | number[]): void => {
                        if (!IsVarEmpty(newValue) && !Array.isArray(newValue)) {
                            handleTelSysChangeByUser(newValue)
                        } else if (Array.isArray(newValue) && newValue.length > 0) {
                            handleTelSysChangeByUser(newValue[0])
                        }
                    }}
                    options={telSysSelectOptions}
                />
            </FormRow>
            <FormRow>
                <ComboBoxAlt
                    multiple
                    label={language.get(ScopeFilteringLanguage.PartitionsLabel)}
                    value={partitions}
                    handleChange={(newValue?: string | string[]): void => {
                        if (!IsVarEmpty(newValue) && Array.isArray(newValue)) {
                            handlePartitionsChange(newValue)
                        }
                    }}
                    options={partitionsSelectOptions}
                />
            </FormRow>
        </Fieldset>
    )
}

export default ScopeFiltering
